import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Eye, EyeOff, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import axios from "axios";
import GoogleSignInButton from "@/components/shared/GoogleSignInButton";
import BackToHomepageLink from "@/components/shared/BackToHomepageLink";
import EarlyAccessBanner from "@/components/shared/EarlyAccessBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { login, token, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetStep, setResetStep] = useState(1); // 1=email, 2=code+newpw, 3=success
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [resetHint, setResetHint] = useState("");

  // Surface Google-signin errors bubbled up by AuthCallbackPage.
  // Format: ?error=google:<status>:<detail>
  useEffect(() => {
    const e = params.get("error") || "";
    if (e.startsWith("google:")) {
      const parts = e.split(":");
      setError(parts.slice(2).join(":") || "Google sign-in failed");
    }
  }, [params]);

  if (token) {
    if (user?.is_admin) { navigate("/admin", { replace: true }); return null; }
    navigate("/dashboard", { replace: true }); return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      const isAdmin = result.user?.role === "admin" || result.user?.is_admin;
      if (isAdmin) {
        navigate("/admin", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (e) {
      // Surface the server's status-specific message for
      // pending_approval / rejected / suspended so users know exactly
      // what state they're in. Fall back to generic credential error.
      // P5 — 423 Locked and 429 Too Many Requests carry a structured
      // detail object; render its `message` directly.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 423 && detail && typeof detail === "object") {
        setError(
          detail.message
          || "Account locked. Try again later or contact support."
        );
      } else if (status === 429 && detail && typeof detail === "object") {
        setError(
          detail.message
          || "Too many attempts. Please wait and try again."
        );
      } else if (detail && typeof detail === "string") {
        setError(detail);
      } else {
        setError("Invalid credentials. Check your email and password.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-[#020617]">
        <img
          src="https://images.unsplash.com/photo-1759176959174-465ad72f3c32?w=1200&q=60"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020617] via-[#020617]/60 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12">
          <div className="flex flex-col gap-4">
            <Link
              to="/"
              className="flex items-center gap-3 hover:opacity-80 transition-opacity w-fit"
              data-testid="login-logo-link"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                <Shield className="w-6 h-6 text-slate-900" />
              </div>
              <div>
                <h1 className="font-barlow font-bold text-xl text-slate-50">Sentient Vault</h1>
                <span className="text-[9px] text-amber-400/70 tracking-[0.25em] uppercase font-manrope">Obligation-Enforced Finance</span>
              </div>
            </Link>
            <BackToHomepageLink testId="login-back-to-homepage" />
          </div>
          <div className="max-w-md">
            <h2 className="font-barlow font-bold text-4xl md:text-5xl text-slate-50 leading-[1.1] mb-4">
              Know what money is <span className="text-amber-400">truly yours.</span>
            </h2>
            <p className="text-slate-400 text-base leading-relaxed">
              The world's first obligation-enforced financial truth system. Classify cash by ownership, not location.
            </p>
          </div>
          <div className="flex gap-8">
            <div>
              <div className="font-mono text-2xl font-bold text-cyan-400">6</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest">Custody Vaults</div>
            </div>
            <div>
              <div className="font-mono text-2xl font-bold text-emerald-400">99.8%</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest">Accuracy</div>
            </div>
            <div>
              <div className="font-mono text-2xl font-bold text-amber-400">10+</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest">Global Cities</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#0B1120]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center justify-between mb-8">
            <Link
              to="/"
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              data-testid="login-logo-link-mobile"
            >
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                <Shield className="w-5 h-5 text-slate-900" />
              </div>
              <span className="font-barlow font-bold text-lg text-slate-50">Sentient Vault</span>
            </Link>
            <BackToHomepageLink
              testId="login-back-to-homepage-mobile"
              label="Home"
            />
          </div>

          <h3 className="font-barlow font-semibold text-2xl text-slate-50 mb-1">Welcome back</h3>
          <p className="text-sm text-slate-500 mb-6">Sign in to your financial command center</p>

          <div className="mb-6">
            <EarlyAccessBanner
              variant="compact"
              testId="login-early-access-banner"
            />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs text-slate-400 uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-900/50 border-slate-700 text-slate-100 h-11"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs text-slate-400 uppercase tracking-wider">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-900/50 border-slate-700 text-slate-100 h-11 pr-10"
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <a href="/forgot-password"
                className="text-xs text-slate-500 hover:text-amber-400 transition-colors" data-testid="forgot-password-link">
                Forgot your password?
              </a>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="login-error">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 font-semibold rounded-lg transition-all active:scale-[0.98]"
              data-testid="login-submit-btn"
            >
              {loading ? "Authenticating..." : "Access Vault"}
            </Button>
          </form>

          {/* Continue with Google — kept SEPARATE from the form so
              that Enter inside the email/password inputs doesn't
              accidentally trigger Google sign-in. */}
          <div className="relative my-5" data-testid="login-or-divider">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-800" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-[0.2em] font-manrope">
              <span className="bg-[#0B1120] px-3 text-slate-600">or</span>
            </div>
          </div>
          <GoogleSignInButton
            label="Continue with Google"
            testId="google-signin-btn-login"
          />

          {/* Forgot Password Flow */}
          {forgotMode && (
            <div className="mt-4 p-4 rounded-xl border border-slate-800/50 bg-slate-900/30" data-testid="forgot-password-flow">
              <button onClick={() => { setForgotMode(false); setResetStep(1); setResetMessage(""); setResetHint(""); }}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300 mb-3">
                <ArrowLeft className="w-3 h-3" /> Back to login
              </button>

              {resetStep === 1 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-200">Reset Your Password</h3>
                  <p className="text-xs text-slate-500">Enter your email and we'll send you a reset code.</p>
                  <Input value={resetEmail} onChange={e => setResetEmail(e.target.value)} placeholder="your@email.com"
                    className="bg-slate-800/50 border-slate-700 text-slate-100 h-10" data-testid="reset-email-input" />
                  <Button onClick={async () => {
                    setResetLoading(true); setResetMessage("");
                    try {
                      const res = await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
                      setResetMessage(res.data.message);
                      setResetHint(res.data.code_hint || "");
                      setResetStep(2);
                    } catch (e) { setResetMessage(e.response?.data?.detail || "Failed"); }
                    setResetLoading(false);
                  }} disabled={resetLoading || !resetEmail} className="w-full h-10 bg-amber-600 hover:bg-amber-500 text-slate-900" data-testid="reset-send-btn">
                    {resetLoading ? "Sending..." : "Send Reset Code"}
                  </Button>
                </div>
              )}

              {resetStep === 2 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-200">Enter Reset Code</h3>
                  <p className="text-xs text-slate-500">A code was sent to {resetEmail}.</p>
                  {resetHint && (
                    <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                      <p className="text-[10px] text-cyan-400 font-mono">Reset code: {resetHint}</p>
                      <p className="text-[9px] text-slate-600">(Shown here because email is simulated)</p>
                    </div>
                  )}
                  <Input value={resetCode} onChange={e => setResetCode(e.target.value)} placeholder="Enter reset code"
                    className="bg-slate-800/50 border-slate-700 text-slate-100 h-10 font-mono" data-testid="reset-code-input" />
                  <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password (min 8 characters)"
                    className="bg-slate-800/50 border-slate-700 text-slate-100 h-10" data-testid="reset-new-pw-input" />
                  <Button onClick={async () => {
                    setResetLoading(true); setResetMessage("");
                    try {
                      const res = await axios.post(`${API}/auth/reset-password`, { email: resetEmail, reset_code: resetCode, new_password: newPassword });
                      setResetMessage(res.data.message);
                      setResetStep(3);
                    } catch (e) { setResetMessage(e.response?.data?.detail || "Reset failed"); }
                    setResetLoading(false);
                  }} disabled={resetLoading || !resetCode || newPassword.length < 8} className="w-full h-10 bg-amber-600 hover:bg-amber-500 text-slate-900" data-testid="reset-confirm-btn">
                    {resetLoading ? "Resetting..." : "Reset Password"}
                  </Button>
                </div>
              )}

              {resetStep === 3 && (
                <div className="text-center space-y-3">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
                  <h3 className="text-sm font-semibold text-emerald-400">Password Reset Successfully</h3>
                  <p className="text-xs text-slate-500">You can now log in with your new password.</p>
                  <Button onClick={() => { setForgotMode(false); setResetStep(1); setPassword(""); }}
                    className="bg-slate-800 hover:bg-slate-700" data-testid="reset-back-login-btn">
                    Back to Login
                  </Button>
                </div>
              )}

              {resetMessage && resetStep !== 3 && (
                <p className="text-xs text-slate-400 mt-2">{resetMessage}</p>
              )}
            </div>
          )}

          <p className="text-center text-sm text-slate-500 mt-4">
            Don't have an account? <a href="/register" className="text-amber-400 hover:text-amber-300">Start free trial</a>
          </p>
          <div className="mt-3 text-center">
            <a href="/demo" className="text-xs text-cyan-400 hover:text-cyan-300 font-mono uppercase tracking-wider" data-testid="try-demo-link">
              Try Interactive Demo
            </a>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-800/30 text-center space-y-1">
            <div><a href="mailto:invitation@celestialmindsfze.com" className="text-[10px] text-slate-600 hover:text-slate-400 transition-colors">Enquiries: invitation@celestialmindsfze.com</a></div>
            <div className="text-[9px] text-slate-700">A Celestial Minds FZE initiative. All rights reserved.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
